import { useMemo } from 'react';

import {
  SearchAndHighlightItem,
  SearchAndHighlightResult
} from 'hooks/useSearchAndHighlight.types';

// basic interfaces

interface SearchValue {
  value: string;
  searchableValue: string;
}

// input interfaces

export interface SearchableItem<T> {
  name: string;
  keywords?: string[];
  data: T; // keep original item data in case its needed somewhere
}

export interface SearchableGroup<T, G> {
  label: string;
  items: SearchableItem<T>[];
  data: G; // keep the original group data in case its needed somewhere
}

export interface UseSearchAndHighlightProps<T, G> {
  groups: SearchableGroup<T, G>[];
  searchValue: string;
}

// modified input interfaces, used inside the hook

export interface SearchableItemExtended<T> {
  name: SearchValue;
  keywords?: SearchValue[];
  data: T;
}

export interface SearchableGroupExtended<T, G> {
  label: string; // group name
  searchableLabel: string; // group searchable value
  items: SearchableItemExtended<T>[];
  data: G;
}

// output interfaces

export interface UseSearchAndHighlightResultItem<T> {
  item: SearchableItemExtended<T>;
  searchResult: SearchAndHighlightResult;
}

export interface UseSearchAndHighlightResultGroup<T, G> {
  label: string;
  items: UseSearchAndHighlightResultItem<T>[]; // search results on individual items are nested here
  data: G;
  searchResult: SearchAndHighlightItem[]; // search result on the group label itself
}

/**
 * Helper function to split text into words for search matching
 * Splits on spaces, hyphens, and other common separators
 */
const splitIntoWords = (text: string): string[] => {
  return text.split(/[\s\-_.,;:!?()[\]{}]+/).filter((word) => word.length > 0);
};

/**
 * Helper function to check if any word in the text starts with any word in the search query
 * This implements the word-based prefix matching requirement
 */
const hasWordBasedMatch = (text: string, searchValue: string): boolean => {
  if (!searchValue || !text) return false;

  const searchWords = splitIntoWords(searchValue.toLowerCase());
  const textWords = splitIntoWords(text.toLowerCase());

  // Check if any word in the text starts with any word in the search query
  return searchWords.some((searchWord) =>
    textWords.some((textWord) => textWord.startsWith(searchWord))
  );
};

/**
 * Helper function to create a search result for an individual item
 */
const createItemSearchResult = <T>({
  item,
  searchValue,
  shouldIncludeNonMatches = false
}: {
  item: SearchableItemExtended<T>;
  searchValue: string;
  shouldIncludeNonMatches?: boolean;
}): UseSearchAndHighlightResultItem<T> | undefined => {
  // 1. Check if item name matches using word-based prefix matching
  if (hasWordBasedMatch(item.name.searchableValue, searchValue)) {
    const titleParts = createHighlightedParts(
      item.name.value,
      item.name.searchableValue,
      searchValue
    );
    return {
      item,
      searchResult: {
        name: titleParts
      }
    };
  }

  // 2. Check if keywords match using word-based prefix matching
  const firstMatchedKeyword = item.keywords?.find((keyword) =>
    hasWordBasedMatch(keyword.searchableValue, searchValue)
  );

  if (firstMatchedKeyword) {
    return {
      item,
      searchResult: {
        name: [{ value: item.name.value, isHighlight: false }],
        keywords: [
          createHighlightedParts(
            firstMatchedKeyword.value,
            firstMatchedKeyword.searchableValue,
            searchValue
          )
        ]
      }
    };
  }

  // 3. If no match but we should include non-matches (group match scenario)
  if (shouldIncludeNonMatches) {
    return {
      item,
      searchResult: {
        name: [{ value: item.name.value, isHighlight: false }]
      }
    };
  }
};

/**
 * Hook that provides search functionality with highlighting for any searchable items
 * This extracts the exact search logic from PathwayStore.orderedPathwayNames
 */
export const useSearchAndHighlight = <T, G>(
  props: UseSearchAndHighlightProps<T, G>
): UseSearchAndHighlightResultGroup<T, G>[] => {
  const searchValue = useMemo(() => props.searchValue.trim().toLowerCase(), [props.searchValue]);

  const groups = useMemo(
    () =>
      props.groups.map<SearchableGroupExtended<T, G>>((group) => ({
        ...group,
        searchableLabel: group.label.trim().toLowerCase(),
        items: group.items
          .map<SearchableItemExtended<T>>((item) => ({
            data: item.data,
            name: {
              value: item.name,
              searchableValue: item.name.trim().toLowerCase()
            },
            keywords: item.keywords?.map((keyword) => ({
              value: keyword,
              searchableValue: keyword.trim().toLowerCase()
            }))
          }))
          // Sort results alphabetically by name - EXACTLY like pathways
          .sort((groupItemA, groupItemB) =>
            groupItemA.name.value.localeCompare(groupItemB.name.value)
          )
      })),
    [props.groups]
  );

  return useMemo(() => {
    if (!searchValue) {
      return [];
    }

    const searchGroupResults: UseSearchAndHighlightResultGroup<T, G>[] = [];

    for (const group of groups) {
      const searchResults: UseSearchAndHighlightResultItem<T>[] = [];
      const shouldIncludeNonMatches = hasWordBasedMatch(group.searchableLabel, searchValue);

      for (const item of group.items) {
        const itemResult = createItemSearchResult({
          item,
          searchValue,
          shouldIncludeNonMatches
        });
        if (itemResult) searchResults.push(itemResult);
      }

      if (searchResults.length > 0) {
        searchGroupResults.push({
          label: group.label,
          items: searchResults,
          data: group.data,
          searchResult: createHighlightedParts(group.label, group.searchableLabel, searchValue)
        });
      }
    }

    return searchGroupResults;
  }, [groups, searchValue]);
};

/**
 * Creates highlighted parts from text based on search value using word-based matching
 * This function breaks down text into parts that can be highlighted
 */
const createHighlightedParts = (
  text: string,
  textLower: string,
  searchValue: string
): SearchAndHighlightItem[] => {
  if (!searchValue) return [{ value: text, isHighlight: false }];

  const parts: SearchAndHighlightItem[] = [];
  const searchWords = splitIntoWords(searchValue.toLowerCase());
  const textWords = splitIntoWords(textLower);

  let currentIndex = 0;
  let highlightedRanges: Array<{ start: number; end: number }> = [];

  // Find all ranges that should be highlighted based on word-based prefix matching
  searchWords.forEach((searchWord) => {
    textWords.forEach((textWord) => {
      if (textWord.startsWith(searchWord)) {
        // Find all occurrences of this text word in the original text
        let searchIndex = 0;
        while (searchIndex < textLower.length) {
          const foundIndex = textLower.indexOf(textWord, searchIndex);
          if (foundIndex === -1) break;

          // Check if this is a word boundary (start of word)
          const isWordStart =
            foundIndex === 0 || /[\s\-_.,;:!?()[\]{}]/.test(textLower[foundIndex - 1]);

          if (isWordStart) {
            // Highlight only the matching prefix part
            const highlightEnd = foundIndex + searchWord.length;
            highlightedRanges.push({
              start: foundIndex,
              end: highlightEnd
            });
          }

          searchIndex = foundIndex + 1;
        }
      }
    });
  });

  // Sort and merge overlapping ranges
  highlightedRanges.sort((a, b) => a.start - b.start);
  const mergedRanges: Array<{ start: number; end: number }> = [];

  for (const range of highlightedRanges) {
    if (mergedRanges.length === 0 || range.start > mergedRanges[mergedRanges.length - 1].end) {
      mergedRanges.push(range);
    } else {
      mergedRanges[mergedRanges.length - 1].end = Math.max(
        mergedRanges[mergedRanges.length - 1].end,
        range.end
      );
    }
  }

  // Create parts based on highlighted ranges
  for (const range of mergedRanges) {
    if (currentIndex < range.start) {
      parts.push({
        value: text.slice(currentIndex, range.start),
        isHighlight: false
      });
    }
    parts.push({
      value: text.slice(range.start, range.end),
      isHighlight: true
    });
    currentIndex = range.end;
  }

  if (currentIndex < text.length) {
    parts.push({
      value: text.slice(currentIndex),
      isHighlight: false
    });
  }

  return parts.length > 0 ? parts : [{ value: text, isHighlight: false }];
};
